<template>
  <div class="kitchen-review-list">
    <div class="kitchen-review-list__container">
      <!-- Life Story Cards Grid -->
      <transition-group
        name="kitchen-review-fade"
        tag="div"
        class="kitchen-review-list__grid">
        <life-story-card
          v-for="review in displayedReviews"
          :key="review.id"
          :slug="review.slug"
          :firstname="review.firstname"
          :message="review.message"
          :date="review.date"
          :kitchen="review.kitchen"
          :image="review.image"
          class="kitchen-review-list__item" />
      </transition-group>

      <!-- Load More CTA -->
      <div
        v-if="hasMoreReviews && !seeAllTo"
        class="kitchen-review-list__load-more">
        <cta
          :text="ctaText"
          variant="text"
          color="red"
          :show-icon="false"
          @click="handleLoadMoreReviews" />
      </div>

      <!-- See All Link -->
      <div
        v-if="seeAllTo"
        class="kitchen-review-list__see-all">
        <cta
          :text="$t('general.seeAll')"
          :to="seeAllTo"
          variant="text"
          color="red"
          :show-icon="false" />
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, ref } from 'vue'

  import Cta from '../Cta.vue'
  import LifeStoryCard from '../LifeStory/Card.vue'

  // Props
  const props = defineProps({
    reviews: {
      type: Array,
      required: true,
      default: () => []
    },
    maxVisible: {
      type: Number,
      default: 6
    },
    showLoadMore: {
      type: Boolean,
      default: true
    },
    ctaText: {
      type: String,
      default: 'VEDI TUTTI'
    },
    seeAllTo: {
      type: [ String, Object ],
      default: null
    }
  })

  // Emits
  const emit = defineEmits([ 'load-more' ])

  // Reactive state
  const showAllReviews = ref(false)

  // Computed properties
  const displayedReviews = computed(() => {
    if (showAllReviews.value || !props.showLoadMore) {
      return props.reviews
    }
    return props.reviews.slice(0, props.maxVisible)
  })

  const hasMoreReviews = computed(() => {
    return props.showLoadMore
      && props.reviews.length > props.maxVisible
      && !showAllReviews.value
  })

  // Methods
  const handleLoadMoreReviews = () => {
    showAllReviews.value = true
    emit('load-more')
  }
</script>

<style lang="scss" scoped>
.kitchen-review-list {
  width: 100%;

  &__container {
    max-width: var(--grid-max-width);
    margin: 0 auto;
    padding: 0 var(--grid-margin);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-xxl);

    @include up-to-tablet {
      padding: 0 var(--space-s);
      gap: var(--space-xl);
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 16px;
    width: 100%;
    transition: all 0.4s ease-out;

    @include mq(medium) {
      grid-template-columns: repeat(3, 1fr);
      gap: 32px;
    }
  }

  &__item {
    opacity: 1;
    transform: translateY(0);
    transition: opacity 0.3s ease-out, transform 0.3s ease-out;
  }

  &__load-more {
    text-align: center;
    margin-top: var(--space-xl);

    @include up-to-tablet {
      margin-top: var(--space-l);
    }
  }

  &__see-all {
    text-align: center;
    margin-top: 80px;

    @include up-to-tablet {
      margin-top: var(--space-xl);
    }
  }
}

/* Minimal transition animations for Vue transition-group */
.kitchen-review-fade-enter-active {
  transition: opacity 0.4s ease-out, transform 0.4s ease-out;
}

.kitchen-review-fade-leave-active {
  transition: opacity 0.3s ease-in, transform 0.3s ease-in;
}

.kitchen-review-fade-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.kitchen-review-fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

.kitchen-review-fade-move {
  transition: transform 0.3s ease-out;
}
</style>
